import { useEffect, useRef, useState } from "react";
import { DownloadingLoopIcon } from "@wptb/ui/icons/loaders";
import store from "@wptb/external/store";
import { setTable, startBuilder } from "@wptb/external/store";
import { parseTable } from "@wptb/external/utils";
import {
  renderTable,
  initializeFunctionality,
  transformTable,
} from "@wptb/external/front-utils";
import { apiGet } from "@wptb/utils/http";
import { stateToFTable } from "../../utils/state-to-f-table";
import { applyCustomStyle } from "@wptb/utils/html";
import { FTable, ResponsiveDeviceProps, TableProps } from "@wptb/dev/types";
import { useSelect } from "@wordpress/data";

interface TableComponentProps {
  id: string;
  shouldLoad: boolean;
  setShouldLoad: (shouldLoad: boolean) => void;
  saveId: number;
}

type ResponsiveConfig = Parameters<typeof transformTable>[1];

const formatConfig = (config: ResponsiveDeviceProps): ResponsiveConfig => {
  return {
    transpose: config.stackDirection === "row",
    repeatFirst: config.enableHead,
    columnCount: config.itemsPerHead + (config.enableHead ? 1 : 0),
  };
};
const getResponsiveConfig = (
  props: TableProps,
  deviceType: string
): ResponsiveConfig | null => {
  if (deviceType === "desktop") {
    return null;
  }
  const mainConfig = props.responsive;
  if (!mainConfig?.enable) return null;
  const { mobile, tablet } = mainConfig;
  if (deviceType === "mobile") {
    if (mobile.disable) return null;
    return formatConfig(mobile);
  }
  if (tablet.disable) return null;
  return formatConfig(tablet);
};

const doRendering = (
  table: FTable,
  el: HTMLElement,
  id: string,
  deviceType: string
) => {
  const responsiveConfig = getResponsiveConfig(table.props, deviceType);
  const renderedTable = renderTable(
    responsiveConfig ? transformTable(table, responsiveConfig) : table,
    Boolean(responsiveConfig),
    id
  );
  el.innerHTML = renderedTable.wrapperHtml;
  el.style.cssText = renderedTable.rootStyle;
  el.className = `wptb-table-${id}`;
  initializeFunctionality(table.props, el);
  applyCustomStyle(table.props.extraStyles, id, el.ownerDocument!);
};

const TablePage = ({
  id,
  shouldLoad,
  setShouldLoad,
  saveId,
}: TableComponentProps) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!shouldLoad) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const abortCtrl = new AbortController();

    apiGet(`/table?id=${id}`, {
      signal: abortCtrl.signal,
    })
      .then((data) => {
        const { table, props, innerBlocks, tableProps } = parseTable(
          data.table
        )!;
        store.dispatch(
          setTable({
            id,
            table: {
              id,
              title: data.name,
              desktop: table.desktop,
              tablet: table.tablet,
              mobile: table.mobile,
            },
            tableProps,
            props,
            innerBlocks,
          })
        );
        store.dispatch(startBuilder(id));
        setIsLoading(false);
        setShouldLoad(false);
      })
      .catch((_) => undefined);

    return () => {
      abortCtrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const deviceType = useSelect(
    // @ts-expect-error not typed yet
    (select) => select("core/editor").getDeviceType()?.toLowerCase(),
    []
  );

  useEffect(() => {
    if (!elRef.current || isLoading) {
      return;
    }
    const table = stateToFTable(id);
    if (!table) {
      return;
    }
    doRendering(table, elRef.current!, id, deviceType);
    applyCustomStyle(
      table.props.extraStyles,
      id,
      elRef.current!.ownerDocument!
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elRef.current, isLoading, id, saveId, deviceType]);

  return isLoading ? (
    <div className="wptb-table-loading-icon">
      <DownloadingLoopIcon />
    </div>
  ) : (
    <div className="wptb-root-app" ref={elRef} />
  );
};

export default TablePage;
